import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Couleurs par défaut disponibles
export const DEFAULT_COLORS = [
  '#4ADE80', // Vert Komby
  '#FFD300', // Jaune Komby
  '#FFC104', // Orange
  '#F43F5E', // Rose
  '#EF4444', // Rouge
];

interface ColorSchemeContextType {
  selectedColors: string[];
  availableColors: string[];
  toggleColor: (color: string) => void;
  clearColors: () => void;
  setColors: (colors: string[]) => void;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

const ColorSchemeContext = createContext<ColorSchemeContextType | undefined>(undefined);

interface ColorSchemeProviderProps {
  children: ReactNode;
}

export const ColorSchemeProvider: React.FC<ColorSchemeProviderProps> = ({ children }) => {
  const [selectedColors, setSelectedColors] = useState<string[]>(() => {
    // Charger depuis localStorage ou utiliser les couleurs par défaut
    const saved = localStorage.getItem('komby-color-scheme');
    return ['#FFD300', '#4ADE80']; // Toujours jaune en premier, ignorer localStorage
  });

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    // Ne sauvegarder que si ce n'est pas les couleurs par défaut
    if (selectedColors[0] !== '#FFD300' || selectedColors[1] !== '#4ADE80') {
      localStorage.setItem('komby-color-scheme', JSON.stringify(selectedColors));
    }
    
    // Mettre à jour les variables CSS
    selectedColors.forEach((color, index) => {
      document.documentElement.style.setProperty(`--accent-${index + 1}`, color);
    });
    
    // Réinitialiser les variables non utilisées
    for (let i = selectedColors.length + 1; i <= 5; i++) {
      document.documentElement.style.setProperty(`--accent-${i}`, DEFAULT_COLORS[i - 1] || DEFAULT_COLORS[0]);
    }
  }, [selectedColors]);

  // Forcer les couleurs par défaut au montage
  useEffect(() => {
    // Forcer le jaune comme couleur principale
    const defaultColors = ['#FFD300', '#4ADE80', '#FFC104', '#F43F5E', '#EF4444'];
    defaultColors.forEach((color, index) => {
      document.documentElement.style.setProperty(`--accent-${index + 1}`, color);
    });
  }, []);

  const toggleColor = (color: string) => {
    setSelectedColors(prev => {
      if (prev.includes(color)) {
        // Retirer la couleur (minimum 1 couleur)
        return prev.length > 1 ? prev.filter(c => c !== color) : prev;
      } else {
        // Ajouter la couleur (maximum 5 couleurs)
        return prev.length < 5 ? [...prev, color] : prev;
      }
    });
  };

  const clearColors = () => {
    setSelectedColors(['#4ADE80']); // Garder au moins la couleur principale
  };

  const setColors = (colors: string[]) => {
    setSelectedColors(colors.slice(0, 5)); // Limiter à 5 couleurs max
  };

  // Couleurs d'accès rapide
  const primaryColor = selectedColors[0] || '#4ADE80';
  const secondaryColor = selectedColors[1] || '#FFD300';
  const accentColor = selectedColors[2] || selectedColors[0] || '#4ADE80';

  const value: ColorSchemeContextType = {
    selectedColors,
    availableColors: DEFAULT_COLORS,
    toggleColor,
    clearColors,
    setColors,
    primaryColor,
    secondaryColor,
    accentColor,
  };

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
};

export const useColorScheme = (): ColorSchemeContextType => {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme must be used within a ColorSchemeProvider');
  }
  return context;
};